import React, { FC, useEffect, useState } from 'react';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { Chip, Grid, Stack } from '@mui/material';
import { SCUIButton, SCUILoadingLinear, SCUINoRecordComponent } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUICustomDataGrid } from '../../atomic/molecules';
import { PostCheckoutQuery } from '../../services/query';
import { SCUITextFieldWithSelectBox, SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIAddLcwMoneyCashInOut } from '../../atomic/molecules/SCUIAddLcwMoneyCashInOut/SCUIAddLcwMoneyCashInOut';
import cachePaymentImage from '../../assets/icons/svg/pageIcons/cashPayment.svg';
import TimeHelper from '../../utils/time';
import { LCWMONEY } from './lcwMoney.classes';
import {
    fetchCustomerIdByEmail,
    fetchWalletTransactionsByCustomerId,
    mapWalletTransactionRows,
} from './LcwMoney.helper';

export const LcwMoney: FC<any> = () => {
    const { t } = useTranslation();
    const columns: ExtendedGridColDef[] = [
        { field: 'id', headerName: 'Id', flex: 1 },
        {
            field: 'amount',
            headerName: 'Amount',
            flex: 1,
            renderCell: (params: any) => {
                const raw: string = String(params?.row?.amount ?? '');
                const [rawValue] = raw.trim().split(' ');
                const numberAmount = parseFloat(rawValue?.replace(',', '.') ?? '0');
                const label = `${params.row.amount}${params.row.currencySymbol && params.row.currencySymbol !== '-' ? ' ' + params.row.currencySymbol : ''}`;
                return (
                    <Chip
                        className={LCWMONEY.datagridAmountChip}
                        label={label}
                        size="small"
                        sx={{
                            backgroundColor: numberAmount > 0 ? '#21bf73' : '#dc3545',
                            color: 'white',
                            fontWeight: 600,
                        }}
                    />
                );
            },
        },
        { field: 'description', headerName: t('general.description'), flex: 1 },
        {
            field: 'dateTime',
            headerName: t('general.date') + ' & ' + t('general.hour'),
            flex: 1,
        },
    ];

    const [searchedText, setSearchedText] = useState<any>('');
    const [documentType, setDocumentType] = React.useState(1);
    const [datas, setDatas] = React.useState<any>();
    const [display, setDisplay] = useState(false);
    const [barDisplay, setBarDisplay] = useState(false);
    const [refresh, setRefresh] = React.useState(false);
    const refreshFunc = () => setRefresh((prevRefreshing) => !prevRefreshing);
    const [open, setOpen] = React.useState(false);
    const [balance, setBalance] = useState(0.0);
    const [customerId, setCustomerId] = useState(0);

    const handleClose = () => {
        setOpen(false);
    };
    const handleClickOpen = () => {
        setOpen(true);
    };

    useEffect(() => {
        if (open === true) {
            if (searchedText.trim().length > 0 && documentType === 1) {
                GetWallet();
            } else {
                SearchCustomerId();
            }
            handleClose();
        }
    }, [refresh]);

    const SearchCustomerId = async (): Promise<any> => {
        try {
            if (!(searchedText.length > 0)) {
                Swal.fire(t('lcwMoney.errors.pleaseEnterData'), ' ', 'error');
                return;
            }

            const res = await fetchCustomerIdByEmail(searchedText);
            const customerIdFromResponse = res.data?.customer?.customerId;
            setCustomerId(customerIdFromResponse);

            if (!customerIdFromResponse) {
                setDisplay(false);
                Swal.fire(t('lcwMoney.errors.noSuchData'));
                return;
            }

            const walletResponse = await fetchWalletTransactionsByCustomerId(customerIdFromResponse);
            setBalance(walletResponse.lcwMoney);

            if (walletResponse.data) {
                setDatas(mapWalletTransactionRows(walletResponse.data));
                setDisplay(true);
                setBarDisplay(false);
            } else {
                Swal.fire(t('lcwMoney.errors.notFound'), ' ', 'error');
                setBarDisplay(false);
            }
        } catch (error) {
            console.log('Hata :>> ', error);
        }
    };

    const GetWallet = async (): Promise<any> => {
        try {
            if (!(searchedText.length > 0)) {
                Swal.fire(t('lcwMoney.errors.pleaseEnterData'), ' ', 'error');
                return;
            }
            let params: any = {};
            params.customerId = searchedText;
            params.walletTypeId = 1;
            PostCheckoutQuery.GetWalletTransactionByCustomerId(params).then((res: any) => {
                setBalance(res.lcwMoney);
                setCustomerId(searchedText);
                if (res.data) {
                    let parseDataCustomerId = res.data.map((item: any) => {
                        return {
                            id: item.transactionId || '-',
                            amount: item.amount || '-',
                            description: item.description || '-',
                            dateTime: TimeHelper.formatDateHour(item.transactionDate) || '-',
                            currencySymbol: item.currencySymbol || '-',
                        };
                    });
                    setDatas(parseDataCustomerId);
                    setDisplay(true);
                    setBarDisplay(false);
                } else {
                    Swal.fire(t('lcwMoney.errors.notFound'), ' ', 'error');
                    setBarDisplay(false);
                }
            });
        } catch (error) {
            setDisplay(false);
            setBarDisplay(false);
            console.log('Hata :>> ', error);
        }
    };

    const sorgula = async () => {
        setDatas(null);
        const containsCharacters = /[a-zA-Z]/.test(searchedText);
        if (documentType === 1 && containsCharacters) {
            Swal.fire({
                title: t('general.error'),
                text: t('lcwMoney.errors.pleaseEnterCorrectCustomerId'),
                icon: 'error',
                showConfirmButton: false,
                timer: 1500,
            });
            return;
        }
        if (documentType === 1) {
            GetWallet();
        } else {
            SearchCustomerId();
        }
    };
    return (
        <SCUIContent>
            <SCUIAddLcwMoneyCashInOut
                balance={balance}
                open={open}
                onClose={handleClose}
                refresh={refreshFunc}
                customerId={customerId}
                e2eClassNames={{
                    dialogPaper: LCWMONEY.cashDialogPaper,
                    dialogTitle: LCWMONEY.cashDialogTitle,
                    progressWrap: LCWMONEY.cashDialogProgressWrap,
                    dialogContent: LCWMONEY.cashDialogContent,
                    amountFieldWrap: LCWMONEY.cashAmountFieldWrap,
                    numericAmount: LCWMONEY.inputNumericCashAmount,
                    description: LCWMONEY.inputTextCashDescription,
                    reference: LCWMONEY.inputTextCashReference,
                    radioWrap: LCWMONEY.cashRadioWrap,
                    radioAdd: LCWMONEY.inputRadioCashAdd,
                    radioRemove: LCWMONEY.inputRadioCashRemove,
                    dialogActions: LCWMONEY.cashDialogActions,
                    saveButton: LCWMONEY.inputButtonCashSave,
                    cancelButton: LCWMONEY.inputButtonCashCancel,
                }}
            />
            <Grid className={LCWMONEY.page} direction="row">
                <div className={LCWMONEY.breadcrumbSection}>
                    <SCUIBreadCrumb title={t('customerInformation.lcwMoney')} componentLink={'LcwMoney'} />
                </div>
                <Stack
                    className={LCWMONEY.toolbarWrap}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 1, sm: 2, md: 4, xl: 20 }}
                >
                    <SCUITextFieldWithSelectBox
                        className={LCWMONEY.searchComposite}
                        selectClassName={LCWMONEY.inputSelectSearchType}
                        inputClassName={LCWMONEY.inputTextSearch}
                        buttonClassName={LCWMONEY.inputButtonSearch}
                        placeholder={' '}
                        buttonText={t('general.search')}
                        selectList={[
                            { value: 1, text: t('lcwMoney.customerId') },
                            { value: 2, text: t('lcwMoney.email') },
                        ]}
                        onChangeSelect={(event: any) => {
                            setSearchedText('');
                            setDatas(null);
                            setDocumentType(event.target.value as number);
                        }}
                        selectValue={documentType}
                        defaultText={searchedText}
                        onChangeTextField={(event: any) => {
                            setSearchedText(event.target.value);
                        }}
                        onClickButton={sorgula}
                        onKeyPressTextField={(e: any) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                sorgula();
                            }
                        }}
                    />
                    {datas && (
                        <SCUIButton
                            className={LCWMONEY.inputButtonCashInOut}
                            text={t('lcwMoney.paraEkleCikar')}
                            type="primary"
                            buttonType="submit"
                            sizeType="fixed"
                            size={'m'}
                            onClick={() => {
                                handleClickOpen();
                            }}
                            style={{ height: 40, marginLeft: '16px' }}
                        />
                    )}
                </Stack>

                <div className={LCWMONEY.loadingSection}>
                    <SCUILoadingLinear display={barDisplay} />
                </div>

                {display && datas && (
                    <Grid className={LCWMONEY.balanceBannerWrap} alignItems="center" justifyContent="center">
                        <h2 className={LCWMONEY.balanceBannerTitle} style={{ color: '#0047ba', marginTop: '16px' }}>
                            {t('lcwMoney.currentBalance')} : {balance}
                            {''} {datas?.[0]?.currencySymbol}
                        </h2>
                    </Grid>
                )}

                {datas ? (
                    <div className={LCWMONEY.gridWrap}>
                        <SCUICustomDataGrid
                            rows={datas}
                            columns={columns}
                            initialState={{ columns: { columnVisibilityModel: { id: false } } }}
                        />
                    </div>
                ) : (
                    <Grid
                        className={LCWMONEY.emptyStateWrap}
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12,
                            xl: 12,
                        }}
                    >
                        <SCUINoRecordComponent img={cachePaymentImage} />
                    </Grid>
                )}
            </Grid>
        </SCUIContent>
    );
};
